// #2423: første import med vilje, men vær præcis om hvad det køber os.
// Entry-chunken, dens STATISKE imports og alle modulepreloads er allerede hentet
// før den første linje JS overhovedet kører — dem kan cookien ikke nå at
// påvirke, og de har heller ikke brug for den: de kom fra samme deployment som
// HTML'en. Det kaldet nedenfor faktisk garanterer, er at `__vdpl` er sat før den
// første LAZY `import()` (React.lazy-ruter og andre dynamiske imports), altså
// præcis de requests der ellers rammer en 404 efter et deploy.
// Se lib/skewProtection.js for mekanikken.
import { installSkewProtection, SKEW_PROTECTION_ENABLED } from "./lib/skewProtection.js";
import "./preview/previewSessionBoot.ts"; // #3430: seed mock-session FØR supabase-js initialiseres (no-op uden VITE_PREVIEW_MOCK)
import React from "react";
import { hydrateRoot, createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import App from "./App.jsx";
import { AppProviders } from "./AppProviders.jsx";
import { initSentry } from "./lib/sentry.jsx";
import {
  accountBootGuardReload,
  installChunkReloadHandlers,
  safeSessionStorage,
} from "./lib/chunkErrors.js";
import { getRelease } from "./lib/release.js";
import { installTranslationResilience } from "./lib/translationResilience.js";
import { captureFirstTouch } from "./lib/attribution.js";
import { installPwa } from "./pwa/installPwa.ts"; // #6584
import { BrowserRouter } from "react-router";
import i18n from "./i18n";
import "./index.css";
// #2047 + #5177: flag-icons-CSS (~ukomprimeret sprite) importeres IKKE globalt her.
// #2047 scopede den til `Flag.jsx` + `LanguageSwitcher.jsx`, men switcheren hænger
// i sidehovedet (Layout.jsx, ikke lazy), så spritet landede alligevel i ENTRY-
// chunkens CSS-graf og blev hentet på hver side (85 KB transfer, render-
// blokerende, målt i #5217). #5177 gav switcheren inline SVG-flag
// (`LanguageSwitcherFlag.jsx`), så sprite-CSS'en nu KUN hænger på `Flag.jsx`, hvis
// forbrugere alle ligger bag lazy ruter.

// Skew Protection (#2423): pin denne klient til det deployment den kører, så en
// lazy chunk hentet EFTER et deploy stadig findes. Første statement, så pinnen
// står før den første dynamiske import. No-op i alt andet end et Vercel
// PRODUCTION-build med Skew Protection slået til.
// HOTFIX 4/9 kl. 11:55 (#2423): cookie-pinnen SLÅET FRA. Målt i prod: Vercel pinner
// asset-requests (Sec-Fetch-Dest: script) til cookiens deployment, men serverer
// DOKUMENTET fra det nyeste deployment ved browser-navigationer. Efter hvert deploy
// fik alle spillere med en cookie fra forrige deploy ny HTML + 404 på alle chunks.
// index.html rydder cookien inline før preloads. Genbesøg i #2423 før gen-tænding.
// `SKEW_PROTECTION_ENABLED` (lib/skewProtection.js) er SSOT for den slukkede
// tilstand — `scripts/check-skew-protection.mjs` læser samme flag for at afgøre
// om dets gate 2/2b skal køre. Sæt ALDRIG flaget til `true` uden eksplicit
// ejer-go (læs begge #2423-postmortems i .claude/learnings/ FØRST).
if (SKEW_PROTECTION_ENABLED) {
  installSkewProtection();
} else {
  void installSkewProtection;
}

// Stale-chunk recovery (#906): et globalt net der fanger dynamic-import/preload-
// fejl efter et deploy FØR React's error-boundary kan ramme dem — både Vite's
// `vite:preloadError` og uhåndterede chunk-rejections (dynamic imports uden for
// React.lazy). Ét kontrolleret reload til frisk index.html, loop-guarded via
// samme per-release sessionStorage-nøgle som boundary'en.
// #3602: reload'en er navigations-guarded — den fyrer kun hvis dokumentet stadig
// kan hente noget, så en chunk-abort forårsaget af en igangværende navigation
// ikke bliver fejllæst som en stale chunk og kaprer navigationen.
// #4595: release-id'et kommer fra <meta name="cz-release"> i den serverede HTML,
// ikke fra import.meta.env. Det er stadig det samme deploy-unikke id (nøglen der
// loop-guarder reloadet pr. release) — men nu står det i HTML'en i stedet for i
// en hashet asset, så et deploy uden frontend-ændringer ikke længere roterer
// asset-navnene. Se lib/release.js.
// #5159 (M3): selve OPSLAGET `window.sessionStorage` kaster i browsere hvor
// site-data er slaaet fra — ikke kun kaldene paa det. Fordi denne linje ligger
// FOER initSentry(), ville en saadan browser doe paa boot uden at nogen fejl
// naaede frem. Alle lag gaar nu gennem den samme sikre accessor.
const _storage = safeSessionStorage(window);
// #5159 (M3): boot-vagt, global fejlhandler, error-boundary og release-watcheren
// deler ÉT recovery-budget. Siden #5440 bogfoerer boot-vagten
// (public/chunk-selfheal.js) selv sit reload dér, foer det sker, og skriver
// markoeren der fortaeller at det er gjort. Kaldet her er no-op for den vagt og
// daekker kun en aeldre, cachet udgave af vagten der blot efterlod sit
// tidsstempel.
accountBootGuardReload(_storage);
const _release = getRelease();
installChunkReloadHandlers({
  target: window,
  release: _release,
  storage: _storage,
  reload: () => window.location.reload(),
});

// #2039: gør DOM-mutation fra browser-oversættelse non-fatal (belt-and-suspenders
// til <html lang>-fixet i LanguageProvider). Skal installeres FØR React renders.
installTranslationResilience();

initSentry();

// #679: snapshot first-touch acquisition source as early as possible — before any
// SPA navigation changes document.referrer. First visit wins; persisted at signup.
captureFirstTouch();

// #6584: PWA-laget (install-lyttere nu, service worker efter load). Se src/pwa/installPwa.ts.
installPwa();

// Preview-mock (#prelive-harness): KUN når VITE_PREVIEW_MOCK er sat (Vercel
// preview-scope). Den dynamiske import bag build-time-guarden ⇒ prod-bundlen
// tree-shaker hele preview/-mappen væk (0 bytes i production). Async IIFE så vi
// kan afvente installeringen FØR mount uden top-level await i entry-modulet.
(async () => {
  if (import.meta.env.VITE_PREVIEW_MOCK) {
    const { installPreviewMock } = await import("./preview/installPreviewMock.js");
    installPreviewMock();
  }

  // i18n initialiseres ved import, men .init() resolver i en microtask. Vent på
  // at den er klar FØR vi monterer — ellers renderer klientens første pass
  // oversættelses-keys (eller tom tekst) hvor den prerendrede markup har fuldt
  // oversat tekst → hydration-mismatch (React #418/#423). Resolver straks hvis
  // allerede initialiseret (inline resources gør den typisk synkron-klar).
  await new Promise((resolve) => {
    if (i18n.isInitialized) resolve();
    else i18n.on("initialized", resolve);
  });

  const rootEl = document.getElementById("root");

  // Hydrér KUN når "/" faktisk ER serveret som den prerendrede landing (#root har
  // markup). På alle andre ruter laver vi en frisk client-render: den tomme
  // app-shell i prod, ELLER et miljø der — uden Vercel-rewriten — fejlserverer
  // landing-index.html for en app-rute (fx `vite preview` i e2e). Uden pathname-
  // gaten ville React forsøge at hydrere landing-markup mod en app-side → mismatch
  // (#418/#422). Vi rydder stale markup før createRoot, så app-ruten ikke arver et
  // glimt af landing.
  const hydratingLanding = Boolean(rootEl.firstElementChild) && window.location.pathname === "/";

  // Den prerendrede landing i dist/index.html renderes ALTID på engelsk
  // (prerender.mjs → render("/", "en")). i18next's LanguageDetector har allerede
  // valgt den besøgendes sprog (localStorage cz_lang → navigator → fallback) FØR
  // vi når hertil, så en da-besøgende ville hydrere DANSK markup mod ENGELSK
  // server-HTML → React #418/#422/#425 (tekst/attribut-mismatch), og prerender-
  // gevinsten smides væk. Vi hydrerer derfor mod EN — identisk med server-HTML —
  // og lader LanguageProvider skifte til det detekterede sprog FØRST efter
  // hydrationen er committet (en effect → ren re-render, ingen mismatch).
  const detectedLang = i18n.language;
  const deferredLanguage =
    hydratingLanding && typeof detectedLang === "string" && !detectedLang.startsWith("en")
      ? detectedLang
      : null;
  if (deferredLanguage) {
    await i18n.changeLanguage("en");
  }

  // Providers ligger i AppProviders, så client-mount og build-time prerender
  // (entry-server.jsx) deler nøjagtig samme træ — det er forudsætningen for ren
  // hydration på landing. `deferredLanguage` er en engangs-hydrerings-hint:
  // LanguageProvider skifter til det efter mount (null på app-ruter/EN-besøgende).
  const tree = (
    <React.StrictMode>
      <AppProviders deferredLanguage={deferredLanguage}>
        {/* #969: transition-wrapped nav (interruptible sidebar-nav) er DEFAULT i
            react-router v8 — den gamle future.v7_startTransition-prop er fjernet
            fra API'et og ville være en stille no-op (fravalg hedder nu
            useTransitions={false}). Routeren bor HER (ikke inde i App), så
            client-træet er identisk med prerenderens (entry-server bruger
            StaticRouter om samme <App/>) → ren hydration. */}
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </AppProviders>
    </React.StrictMode>
  );

  if (hydratingLanding) {
    hydrateRoot(rootEl, tree);
  } else {
    if (rootEl.firstElementChild) rootEl.replaceChildren();
    createRoot(rootEl).render(tree);
  }

  // #4595 review: appen har nu booted — boot-vagten i public/chunk-selfheal.js
  // skal stoppe med at reagere paa modul-fejl herefter (en fejl efter et
  // vellykket mount er ikke et boot-problem, og haandteres af andre lag som
  // lazyWithRetry.js).
  window.__czAppBooted = true;

  // #4595 review: spor selv-helbredte deploys i Sentry som et lavstoej
  // "warning"-signal, saa vi kan se hvor tit boot-vagten reddede en session
  // uden at forurene fejl-raten. Rapporteres hoejst én gang pr. session (egen
  // sessionStorage-nøgle), og kun hvis vagtens tidsstempel er friskt (<5 min) —
  // en gammel noegle fra en tidligere session i samme fane er ikke "lige skete".
  try {
    const selfHealAt = Number(_storage?.getItem("cz_chunk_selfheal_at"));
    const alreadyReported = _storage?.getItem("cz_chunk_selfheal_reported");
    if (selfHealAt && !alreadyReported && Date.now() - selfHealAt < 5 * 60 * 1000) {
      Sentry.captureMessage("chunk-selfheal reloaded", {
        level: "warning",
        extra: { pathname: window.location.pathname, referrer: document.referrer },
      });
      _storage?.setItem("cz_chunk_selfheal_reported", "1");
    }
  } catch {
    // sessionStorage utilgaengelig — samme fail-closed holdning som selve vagten.
  }
})();
